import type { ReactNode } from "react";
import type { AcceptanceResult, AutoDelivery, AutoDeliveryState, AutoTaskProgressRow, AutoTaskState } from "../types";

const STATE_LABEL: Record<AutoTaskState, string> = {
	running: "运行中",
	awaiting_review: "待确认",
	waiting_quota: "额度已用尽",
	passed: "已通过",
	failed: "失败",
	aborted: "已中止",
	interrupted: "已中断",
	started: "已启动",
	finished: "已结束",
	skipped: "未接手",
	paused: "已暂停接手",
	launching: "启动中",
	launch_failed: "启动失败",
};

const DELIVERY_LABEL: Record<AutoDeliveryState, string> = {
	queued: "待推送",
	integrating: "合入检查中",
	paused: "推送已暂停",
	pushed: "已推送 · 待部署",
	deployed: "已部署",
	deploy_failed: "部署失败",
	withdrawn: "已撤出推送",
};

/** The last lines of a check's output shown here; its Loop shows the rest. */
const OUTPUT_LINES = 12;

/**
 * One task's auto progress (CONTEXT.md "Auto progress"): its state and why, its Loop's checks, where its
 * change stands in the push lane and a link to its Loop. Only an interrupted run, a Loop or a one-shot, is recovered from here.
 */
export function AutoTaskProgress({ row, title, onResume }: { row: AutoTaskProgressRow; title?: ReactNode; onResume?: () => void }) {
	const resumable = row.state === "interrupted" && row.loopId !== null;
	return (
		<div className="auto-progress">
			<div className="auto-progress-head">
				{title}
				<span className={`pill pill-${row.state}`}>{STATE_LABEL[row.state] ?? row.state}</span>
			</div>
			<p className="loop-meta">
				{row.change === null ? (
					"未关联改动"
				) : (
					<>
						改动 <code>{row.change}</code>
					</>
				)}
				{row.execution && (
					<>
						{" "}
						· 分支 <code>{row.execution.branch}</code>
					</>
				)}
				{row.execution?.kind === "one-shot" && (
					<>
						{" "}
						<span className="pill">one-shot</span>
					</>
				)}
				{row.loopId && (
					<>
						{" "}
						· <a href={`/?loop=${encodeURIComponent(row.loopId)}`}>打开 Loop</a>
					</>
				)}
			</p>
			{row.reason && <p className="auto-progress-reason">{row.reason}</p>}
			{row.acceptance.length > 0 && <AcceptanceChecks results={row.acceptance} />}
			{row.delivery && <DeliveryView delivery={row.delivery} />}
			{resumable && onResume && (
				<div className="row">
					<button onClick={onResume}>恢复执行</button>
				</div>
			)}
		</div>
	);
}

/** A Loop's checks folded to a count, opened on the first that failed. */
export function AcceptanceChecks({ results }: { results: AcceptanceResult[] }) {
	const failing = results.find((r) => !r.passed);
	const passed = results.filter((r) => r.passed).length;
	return (
		<details className="auto-checks" open={failing !== undefined}>
			<summary>
				验收 {passed}/{results.length}
				{failing ? "" : " 通过"}
			</summary>
			{results.map((r, i) => (
				<CheckView key={i} result={r} open={r === failing} />
			))}
		</details>
	);
}

function CheckView({ result, open }: { result: AcceptanceResult; open: boolean }) {
	const tail = result.output.split("\n").slice(-OUTPUT_LINES).join("\n");
	return (
		<details className="acceptance" open={open}>
			<summary>
				<code>{result.run ?? result.type}</code> {result.passed ? "✓" : `✗ exit ${result.exitCode ?? "none"}`}
				{result.error && ` (${result.error})`}
			</summary>
			<pre className="loop-output">{tail || "（无输出）"}</pre>
		</details>
	);
}

/**
 * Where a passed change stands in the push lane (CONTEXT.md "Push lane"), and the checks its integration ran;
 * brief leaves out the name and the reason, which a ticket's page says above it.
 */
export function DeliveryView({ delivery, brief = false }: { delivery: AutoDelivery; brief?: boolean }) {
	const failing = delivery.checks?.some((c) => !c.passed) ?? false;
	const passed = delivery.checks?.filter((c) => c.passed).length ?? 0;
	return (
		<div className="auto-progress-delivery">
			<p>
				{!brief && "推送 "}
				<span className={`pill pill-lane-${delivery.state}`}>{DELIVERY_LABEL[delivery.state] ?? delivery.state}</span>
				{delivery.pushedSha && (
					<>
						{" "}
						<code>{delivery.pushedSha.slice(0, 8)}</code>
					</>
				)}
			</p>
			{!brief && (delivery.reason ?? delivery.pause) && <p className="auto-progress-reason">{delivery.reason ?? delivery.pause}</p>}
			{delivery.checks?.length > 0 && (
				<details className="auto-checks" open={failing}>
					<summary>
						合入检查 {passed}/{delivery.checks.length}
						{failing ? "" : " 通过"}
					</summary>
					<ul className="auto-progress-checks">
						{delivery.checks.map((c, i) => (
							<li key={i}>
								<code>{c.run}</code> {c.passed ? "✓" : "✗"}
							</li>
						))}
					</ul>
				</details>
			)}
		</div>
	);
}
